/**
 * Board 3 D · Withdraw quote (440) — `docs/job-stages.md` §5, §8 D: take a
 * sent or viewed quote back. It closes as **Expired** (`withdrawn`, the *Why*
 * in `status_reason`), the customer's link shows the expired page, and the job
 * goes back to **Requested**.
 *
 * **Why** is required; **Withdraw** (danger) waits for it. **Keep it** is the
 * way out. `POST /quotes/:id/withdraw {reason}`.
 */
import { useEffect, useState } from 'react'
import type { JobActionsAnswer } from '@rowted/shared'
import { formatDateOnly } from '../lib/format.js'
import { readJobActions, withdrawQuote } from './api.js'
import { COMMON, WITHDRAW, quoteVersion } from './copy.js'
import { FieldLabel, Fields, Refusal, StageBand, StagePopup, TextBox, messageOf } from './popup.js'
import { hasWhy } from './rules.js'

export interface WithdrawQuoteProps {
  open: boolean
  /** The job the quote is on — its quotes carry the dates the sub prints. */
  jobId: string
  /** The quote with the customer. */
  quoteId: string
  onClose: () => void
  /** After the server withdrew it: the quote, and the *Why*. */
  onDone: (answer: { quoteId: string; reason: string }) => void
  phone?: boolean
}

export function WithdrawQuote(props: WithdrawQuoteProps) {
  if (!props.open) return null
  return <WithdrawQuoteBox {...props} />
}

function WithdrawQuoteBox({ jobId, quoteId, onClose, onDone, phone }: WithdrawQuoteProps) {
  const [facts, setFacts] = useState<JobActionsAnswer | null>(null)
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    readJobActions(jobId, quoteId)
      .then((answer) => { if (live) setFacts(answer) })
      .catch((cause: unknown) => { if (live) setError(messageOf(cause)) })
    return () => { live = false }
  }, [jobId, quoteId])

  const quote = facts?.quotes.find((one) => one.id === quoteId) ?? null

  async function withdraw() {
    if (!hasWhy(why)) return
    setBusy(true)
    setError(null)
    try {
      await withdrawQuote(quoteId, why.trim())
      onDone({ quoteId, reason: why.trim() })
    } catch (cause) {
      setError(messageOf(cause))
    } finally {
      setBusy(false)
    }
  }

  const band = WITHDRAW.band(facts?.firstName)
  const sub = quote?.sentOn
    ? COMMON.sentViewed(formatDateOnly(quote.sentOn), quote.viewedOn ? formatDateOnly(quote.viewedOn) : null)
    : undefined

  return (
    <StagePopup
      width={440}
      title={quote ? WITHDRAW.title(quoteVersion(quote.reference, quote.version)) : ''}
      {...(sub ? { sub } : {})}
      tone="grey"
      glyph="rotate"
      onClose={onClose}
      asDrawn="js-03 D"
      {...(phone === undefined ? {} : { phone })}
      buttons={[
        { label: WITHDRAW.keep, tone: 'plain', onClick: onClose },
        { label: WITHDRAW.withdraw, tone: 'danger', disabled: busy || !hasWhy(why), onClick: () => void withdraw() },
      ]}
    >
      <Fields>
        <div className="min-w-0">
          <FieldLabel htmlFor="withdraw-why" label={WITHDRAW.why} required />
          <TextBox id="withdraw-why" value={why} onChange={setWhy} autoFocus />
        </div>
        <StageBand tone="grey">
          <b>{band.lead}</b> {band.rest}
        </StageBand>
        <Refusal message={error} />
      </Fields>
    </StagePopup>
  )
}
